/**
 * Brand Configuration — MADGE Logistics, LLC
 *
 * Updated v2.0 — Orange/Yellow palette with blue interactive accents.
 * Rebranded from original navy/yellow scheme per client direction.
 */

export const brand = {
  name: "MADGE Logistics",
  fullName: "MADGE Logistics, LLC",
  tagline: "Affordable Shipping Made Easy",
  description:
    "Serving families from Florida & New York to Jamaica & the Caribbean. We make it simple, safe, and cost-effective to send your packages, whether big or small, across the sea.",

  // Colors — Updated v2.0 palette
  colors: {
    // Primary (Orange family)
    primary: "#EA580C",
    primaryDark: "#C2410C",
    primaryLight: "#FED7AA",
    primaryLighter: "#FFF7ED",
    // Accent (Yellow/Amber family)
    accent: "#FBBF24",
    accentDark: "#D97706",
    accentLight: "#FEF3C7",
    // Interactive (Blue — strategic accents for CTAs, badges, links)
    interactive: "#2563EB",
    interactiveDark: "#1D4ED8",
    interactiveLight: "#FED7AA",
    // Neutrals
    black: "#000000",
    dark: "#1C1410",       // warm dark for panels
    darkAlt: "#2A1A0F",    // slightly lighter dark panel
    gray: "#57534E",       // stone-600 body text
    lightGray: "#F5F5F4",  // stone-100 section backgrounds
    cream: "#FFFBEB",      // amber-50 warm off-white
    white: "#FFFFFF",
    // Status
    success: "#16A34A",
    warning: "#F59E0B",
    danger: "#DC2626",
  },

  // Logo concept
  logo: {
    concept: "Bold uppercase MADGE in orange with LOGISTICS beneath. Yellow accent strip with tagline. Blue interactive elements.",
    iconType: "shipping",
    primaryColor: "#EA580C",
    accentColor: "#FBBF24",
  },

  // Contact (updated v2.0)
  contact: {
    phone: "(917) 749-9597",
    instagram: "@madge_logistics",
    email: "MadgelogisticsJA@gmail.com",
    floridaAddress: {
      street: "10791 NW 53rd Street, Suite 115",
      city: "Sunrise",
      state: "FL",
      zip: "33351",
    },
    newYorkAddress: null as any,
    jamaicaOffice: "Jamaica & the Caribbean",
  },

  // Business hours
  hours: {
    monday_friday: "9:00 AM - 5:00 PM",
    saturday: "10:00 AM - 3:00 PM",
    sunday: "Closed",
    note: "Holiday hours may vary",
  },

  // Services
  services: [
    {
      icon: "plane",
      title: "Air & Sea Freight Options",
      description: "Flexible solutions to match your budget and timeline.",
    },
    {
      icon: "truck",
      title: "Door-to-Door Delivery",
      description: "Convenient pickups and deliveries directly to your location.",
    },
    {
      icon: "shield",
      title: "Customs Clearance — No Hidden Fees",
      description: "Complete transparency, with all costs communicated upfront.",
    },
    {
      icon: "package",
      title: "Drop Shipping Service",
      description: "Ideal for customers in Jamaica without a U.S. address.",
    },
    {
      icon: "cart",
      title: "Purchase Assistance",
      description: "We can make purchases on your behalf if you don't have a credit card.",
    },
  ],

  // Why choose us
  whyChooseUs: [
    { title: "Cost-Effective Shipping", description: "Designed with families in mind." },
    { title: "Transparent Pricing", description: "No surprises when it comes to customs or service fees." },
    { title: "Friendly, Family-Oriented Service", description: "You're more than just a number." },
    { title: "Live Receptionist & Quick Response", description: "Always talk to a real person." },
    { title: "Tracking Updates", description: "Receive real-time notifications by email or text so you know where your shipment is every step of the way." },
  ],

  // Routes served
  routes: "Florida & New York → Jamaica & the Caribbean",

  // Promo
  promo: "First-Time Customer Discount Available!",
  promoFooter: "Let MADGE Logistics help keep your family connected across the sea — with affordable, reliable shipping you can trust.",

  // Social
  social: {
    instagram: "@madge_logistics",
  },

  // US Warehouse address
  warehouse: {
    name: "MADGE Logistics",
    street: "10791 NW 53rd Street, Suite 115",
    city: "Sunrise",
    state: "FL",
    zip: "33351",
    country: "USA",
  },
} as const;

export type Brand = typeof brand;
